import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  Separator,
} from "@/components/ui";
import { UniversalClinicCarePlanPreviewContent } from "@/features/Charting/components/VisitClosure/CarePlanPreviewStep/UniversalClinicCarePlanPreviewContent";
import { useEffect } from "react";
import { useUniversalClinicCarePlanPreview } from "./network/useUniversalClinicCarePlanPreview";

interface UniversalClinicCarePlanPreviewModalProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  carePlanIds: string[];
  onSendCarePlan?: () => void | Promise<void>;
  isSending?: boolean;
  sendButtonLabel?: string;
  /** When true, allows sending even if care plans have no content */
  allowSendWithoutCareContent?: boolean;
}

export function UniversalClinicCarePlanPreviewModal({
  open,
  onOpenChange,
  carePlanIds,
  onSendCarePlan,
  isSending = false,
  sendButtonLabel = "Send Care Plan",
  allowSendWithoutCareContent = false,
}: UniversalClinicCarePlanPreviewModalProps) {
  const {
    mutate: fetchPreview,
    data,
    isPending: isLoading,
    isError,
    reset,
  } = useUniversalClinicCarePlanPreview();

  // Check if all careplans have care content
  const hasCareContent =
    (data?.careplans?.length ?? 0) > 0 &&
    data?.careplans?.every((cp) => cp.hasCareContent);

  // Fetch preview when modal opens
  useEffect(() => {
    if (open && carePlanIds.length > 0) {
      fetchPreview({ careplanIds: carePlanIds });
    }
  }, [open, carePlanIds, fetchPreview]);

  // Reset mutation state when modal closes
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSendCarePlan = async () => {
    if (onSendCarePlan) {
      await onSendCarePlan();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          className="w-full max-w-md"
          runOnDialogCloseIconClick={handleClose}
          onInteractOutside={handleClose}
        >
          <DialogHeader>
            <DialogTitle>Care Plan Preview</DialogTitle>
            <DialogDescription className="hidden">
              care plan preview modal
            </DialogDescription>
          </DialogHeader>
          <Separator />
          <UniversalClinicCarePlanPreviewContent
            carePlanIds={carePlanIds}
            carePlans={data?.careplans}
            isLoading={isLoading}
            isError={isError}
            hasCareContent={hasCareContent}
            onSendCarePlan={onSendCarePlan ? handleSendCarePlan : undefined}
            isSending={isSending}
            sendButtonLabel={sendButtonLabel}
            allowSendWithoutCareContent={allowSendWithoutCareContent}
          />
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
